import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import { useQuery, useMutation } from '@apollo/client';
import { Box, BoxProps, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MastercodeUnlockAnnouncement from 'components/@common/Announcements/MastercodeUnlockAnnouncement';
import ContactUsPanel from 'components/@common/ContactUsPanel';
import PageContainer from 'components/@layout/PageContainer';
import Config from 'config';
import GET_LOCKED_MASTERCODES from 'graph/orders/get-locked-mastercodes-query';
import SAVE_USER_SETTING_MUTATION from 'graph/settings/settings-user/save-user-setting';
import { UserSetting } from 'types/schema-generated-types';
import { PliUserSetting } from 'store/types';
import useUserStore from 'store/user';
import updateUserSettingCache from 'store/user/update-user-setting-cache';
import { ModuleProps } from 'types';
import EndOfYearProcessingPanel from '../../EndOfYearProcessing/components/EndOfYearProcessingPanel';
import HeaderBar from '../HeaderBar';
import BulkImport from './components/BulkImport';
import ClassLink from './components/ClassLink';
import Clever from './components/Clever';
import ManualEntry from './components/ManualEntry';
import ResourcesBox from './components/ResourcesBox';
import RosteringTypeSelectInput, { RosteringType } from './components/RosteringTypeSelectInput';

export type RosteringTypeSelectProps = Omit<BoxProps, 'onChange'> & {
  onChange: (event: React.ChangeEvent<{ name?: string; value: unknown }>) => void;
  value?: string;
  inputClassName?: string;
};

const DISTRICT_ROSTERING = [Clever, ClassLink, BulkImport, ManualEntry];
const PERSONAL_ROSTERING = [ManualEntry, BulkImport];

export const getRosteringType = (settings: UserSetting[] = []): string | undefined =>
  settings.find(s => s.setting === PliUserSetting.ROSTERING_TYPE)?.value;

const AdminRosterManagement = (props: ModuleProps) => {
  const classes = useStyles();
  const { data, error } = useQuery(GET_LOCKED_MASTERCODES, {
    fetchPolicy: 'cache-and-network'
  });

  return (
    <PageContainer error={error} {...props}>
      <HeaderBar {...props} />
      <Box className={classes.root}>
        <EndOfYearProcessingPanel />
        <MastercodeUnlockAnnouncement href="/dashboard/orders" mastercodes={data?.mastercodes} />
        <RosterManagementContent />
        <ContactUsPanel contactUsUrl={Config.rosteringSupportUrl} />
      </Box>
    </PageContainer>
  );
};

const RosterManagementContent = () => {
  const classes = useStyles();
  const { t } = useTranslation('AdminRosterManagement');
  const [{ settings }] = useUserStore();

  const defaultType = getRosteringType(settings) ?? '';
  const [type, setType] = useState(defaultType);

  const [updateUserSetting] = useMutation(SAVE_USER_SETTING_MUTATION, {
    update(cache, { data }) {
      updateUserSettingCache(cache, data?.updatePliUserSetting);
    }
  });

  const districtResources = [
    {
      href: `${Config.s3Url}/docs/LWT_Digital_Products_School_and_District_Setup_Guide.pdf`,
      label: t('setupInstructionsLabel')
    }
  ];
  const personalResources = [
    {
      href: `${Config.s3Url}/docs/LWT_Digital_Products_Parent_and_Family_Setup_Guide.pdf`,
      label: t('setupInstructionsLabel')
    }
  ];

  const handleRosteringTypeChange = async (
    event: React.ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    const newType = event.target.value as string;

    try {
      await updateUserSetting({
        variables: { data: { setting: PliUserSetting.ROSTERING_TYPE, value: newType } }
      });
    } catch {
      // Ignore, state gets updated regardless.
    }

    setType(newType);
  };

  if (!type) {
    return (
      <RosteringTypeSelect
        value={type}
        onChange={handleRosteringTypeChange}
        inputClassName={classes.input}
        className={classes.contentContainer}
      />
    );
  }

  let title = t('personalOptionsTitle');
  let resourcesTitle = t('personalSetupResources');
  let resources = personalResources;
  let options = PERSONAL_ROSTERING;

  if (type === RosteringType.DISTRICT) {
    title = t('districtOptionsTitle');
    resourcesTitle = t('districtSetupResources');
    resources = districtResources;
    options = DISTRICT_ROSTERING;
  }

  return (
    <>
      <RosteringTypeSelect
        value={type}
        onChange={handleRosteringTypeChange}
        inputClassName={classes.input}
        className={classes.inlineInputContainer}
      />
      <ResourcesBox title={resourcesTitle} resourceLinks={resources} />
      <Typography variant="h2" className={classes.title}>
        {title}
      </Typography>
      {options.map((OptionComponent, index) => (
        <OptionComponent key={index} />
      ))}
    </>
  );
};

const RosteringTypeSelect = ({
  value = '',
  onChange,
  inputClassName,
  ...boxProps
}: RosteringTypeSelectProps) => {
  const { t } = useTranslation('AdminRosterManagement');

  return (
    <Box {...boxProps}>
      <Typography variant="h2">{t('rosteringTypeLabel')}</Typography>
      <Box className={cx(inputClassName)}>
        <RosteringTypeSelectInput fullWidth value={value} onChange={onChange} />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    gap: `${theme.spacing(3)}px`,
    padding: `${theme.spacing(3)}px ${theme.spacing(5)}px`
  },
  contentContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: `${theme.spacing(2)}px`
  },
  inlineInputContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${theme.spacing(1)}px`
  },
  title: {
    marginTop: theme.spacing(4)
  },
  input: {
    width: 400
  }
}));

export default AdminRosterManagement;
